import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

import { getBankLabel } from '../../constants/thaiBanks';
const API = process.env.REACT_APP_API_URL;

// ค่าเริ่มต้น (hardcode ใน backend expenses.service.js) — แสดงให้ HR เห็นว่าถ้าไม่ตั้งค่าจะใช้ใคร
const DEFAULTS = {
  main: { name: 'บริษัท โซตัส เอ็นจิเนียริ่ง จำกัด', bank: 'Krungthai Bank (KTB)', account: '9156040792' },
  external: { name: 'บริษัท ซี.ดี. จำกัด', bank: 'Krungthai Bank(KTB)', account: '9156040768' },
};

const describe = (c) => (c
  ? `${(c.bank?.account_name || c.name || '').trim()} — ${getBankLabel(c.bank?.bank_name) || 'ไม่มีข้อมูลธนาคาร'} ${c.bank?.account_number || ''}`.trim()
  : null);

// เลือก Customer ผู้รับเงิน 1 ช่อง (ค้นหาจากชื่อ) — ว่าง = ใช้ค่าเริ่มต้น
function CustomerPicker({ label, helper, value, onChange, fallback }) {
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get(`${API}/customer`, { params: { name: input, size: 30, page: 1 } })
        .then(({ data }) => { if (!cancelled) setOptions(data?.rows || []); })
        .catch(() => { if (!cancelled) setOptions([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [input]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{helper}</Typography>
      <Autocomplete
        size="small"
        options={options}
        loading={loading}
        value={value}
        onChange={(_, v) => onChange(v)}
        onInputChange={(_, v) => setInput(v)}
        filterOptions={(x) => x}
        isOptionEqualToValue={(o, v) => String(o._id || o.id) === String(v._id || v.id)}
        getOptionLabel={(o) => (o ? (o.name || '').trim() : '')}
        renderOption={(props, o) => (
          <li {...props} key={o._id || o.id}>
            <Box>
              <Typography variant="body2">{(o.name || '').trim()}</Typography>
              <Typography variant="caption" color="text.secondary">
                {o.bank?.bank_name ? `${getBankLabel(o.bank.bank_name)} ${o.bank.account_number || ''}` : 'ไม่มีข้อมูลธนาคาร'}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="ค้นหาชื่อ Customer — ปล่อยว่างเพื่อใช้ค่าเริ่มต้น"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: value ? '#15803d' : '#6b7280' }}>
        {value
          ? `ผู้รับเงินที่จะใช้: ${describe(value)}`
          : `ไม่ได้ตั้งค่า — ใช้ค่าเริ่มต้น: ${fallback.name} — ${fallback.bank} ${fallback.account}`}
      </Typography>
    </Box>
  );
}

// ตั้งค่า Customer (ผู้รับเงิน) ของรายการเบิกค่าแรงที่ระบบสร้างอัตโนมัติจากหน้าสรุปเงินเดือน — /humen/labor-payee-settings
// ใช้กับ "รายการที่สร้างใหม่" เท่านั้น รายการที่สร้างไปแล้วไม่ถูกแก้ผู้รับเงิน
export default function HrLaborPayeeSettings() {
  const [main, setMain] = useState(null);
  const [external, setExternal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/information/labor-payee`);
      setMain(data?.customer || null);
      setExternal(data?.external_customer || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/information/labor-payee`, {
        customer: main ? (main._id || main.id) : null,
        external_customer: external ? (external._id || external.id) : null,
      });
      showAlert('บันทึกการตั้งค่าผู้รับเงินเรียบร้อย — มีผลกับรายการเบิกค่าแรงที่สร้างใหม่จากนี้ไป');
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 760, py: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>ตั้งค่าผู้รับเงินค่าแรง</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        เลือก Customer ที่เป็นผู้รับเงินของรายการเบิกค่าแรงที่ระบบสร้างอัตโนมัติจากหน้าสรุปเงินเดือน
        ถ้าไม่ตั้งค่า ระบบจะใช้ค่าเริ่มต้นที่กำหนดไว้ในระบบ — มีผลกับรายการที่สร้างใหม่เท่านั้น รายการที่สร้างไปแล้วไม่เปลี่ยน
      </Typography>
      <Card variant="outlined">
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          ) : (
            <Stack spacing={3}>
              <CustomerPicker
                label="ผู้รับเงินรายการค่าแรงปกติ (รายโครงการ)"
                helper="รายการเบิกค่าแรงของแต่ละโครงการ รวมพนักงานทุกคนที่ไม่ได้ติ๊ก “ไม่จ่ายเงินตามโครงการ”"
                value={main}
                onChange={setMain}
                fallback={DEFAULTS.main}
              />
              <CustomerPicker
                label="ผู้รับเงินรายการพนักงานที่ “ไม่จ่ายเงินตามโครงการ”"
                helper="รายการเบิกค่าแรงที่แยกเป็นรายคน (ชื่อพนักงานอยู่ในชื่อรายการ) ของพนักงานที่ติ๊ก “ไม่จ่ายเงินตามโครงการ” ในข้อมูลพนักงาน"
                value={external}
                onChange={setExternal}
                fallback={DEFAULTS.external}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={load} disabled={saving}>ยกเลิกการแก้ไข</Button>
                <Button variant="contained" onClick={save} disabled={saving}>
                  {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
